import { useCallback, useEffect, useRef, useState } from 'react'
import { Paperclip, SendHorizontal } from 'lucide-react'
import { Textarea } from '@/shared/components/ui/textarea'
import { Button } from '@/shared/components/ui/button'
import { toast } from 'sonner'
import {
  CHAT_COPY,
  chatErrorMessage,
  useChatOutboxStore,
  useChatUiStore,
  useChatUserId,
  useSendMessage,
  useUploadAttachment,
  type ChatMember,
} from '@/entities/chat'
import { MAX_ATTACHMENT_BYTES, imageDimensions, mimeForFile, newClientRef } from './chatHelpers'
import { QuickReplyChips } from './QuickReplyChips'
import { ComposerAttachments } from './ComposerAttachments'
import { MentionPopover } from './MentionPopover'

const FILE_TYPE_REFUSED =
  "That file type can't be attached. Use a photo, PDF, Office document, text file or short video."
const FILE_TOO_LARGE = 'Attachments can be up to 25 MB.'

export function ChatComposer({
  conversationId,
  members,
  hintText,
  replyPreview,
  canPost,
  showChips,
  onSendAndAdvance,
}: {
  conversationId: number
  members: ChatMember[]
  hintText: string
  replyPreview: string | null
  canPost: boolean
  showChips: boolean
  onSendAndAdvance?: () => void
}) {
  const myUserId = useChatUserId()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [rateLimitCopy, setRateLimitCopy] = useState<string | null>(null)

  const draft = useChatOutboxStore((state) => state.drafts[conversationId])
  const setDraft = useChatOutboxStore((state) => state.setDraft)
  const setReplyTo = useChatOutboxStore((state) => state.setReplyTo)
  const stageFiles = useChatOutboxStore((state) => state.stageFiles)
  const patchStaged = useChatOutboxStore((state) => state.patchStaged)
  const clearComposer = useChatOutboxStore((state) => state.clearComposer)

  const upload = useUploadAttachment()
  const send = useSendMessage()

  const body = draft?.body ?? ''
  const staged = draft?.staged ?? []
  const hasUploadingAttachment = staged.some((item) => item.uploading)
  const mentionable = members.filter(
    (member) => member.role === 'Staff' && member.userId != null && member.userId !== myUserId,
  )
  const mentionMatches =
    mentionQuery !== null
      ? mentionable.filter((member) => member.name.toLowerCase().includes(mentionQuery.toLowerCase()))
      : []

  const acceptMention = useCallback(
    (member: ChatMember) => {
      setDraft(conversationId, body.replace(/@(\w*)$/, `@${member.name} `))
      setMentionQuery(null)
      textareaRef.current?.focus()
    },
    [body, conversationId, setDraft],
  )

  const replyPreviewSettledRef = useRef(false)
  useEffect(() => {
    if (!replyPreviewSettledRef.current) {
      replyPreviewSettledRef.current = true
      return
    }
    if (replyPreview) textareaRef.current?.focus()
  }, [replyPreview])

  const openFocusSettledRef = useRef(false)
  useEffect(() => {
    if (openFocusSettledRef.current) return
    openFocusSettledRef.current = true
    if (!useChatUiStore.getState().composerFocusRequested) return
    useChatUiStore.getState().setComposerFocusRequested(false)
    textareaRef.current?.focus()
  }, [])

  const stage = useCallback(
    async (files: File[]) => {
      if (!canPost) return
      const entries = files.slice(0, 10 - staged.length).map((file) => {
        const contentType = mimeForFile(file)
        const error =
          contentType === null ? FILE_TYPE_REFUSED : file.size > MAX_ATTACHMENT_BYTES ? FILE_TOO_LARGE : null
        return {
          id: newClientRef(),
          file,
          contentType: contentType ?? '',
          attachmentId: null,
          attachment: null,
          error,
          uploading: error === null,
        }
      })

      if (entries.length === 0) return
      stageFiles(conversationId, entries)

      for (const entry of entries) {
        if (entry.error) continue
        try {
          const dimensions = await imageDimensions(entry.file)
          const result = await upload.mutateAsync({
            conversationId,
            file: entry.file,
            contentType: entry.contentType,
            dimensions,
          })
          patchStaged(conversationId, entry.id, {
            attachmentId: result.attachmentId,
            attachment: {
              fileId: result.fileId,
              name: result.name,
              contentType: result.contentType,
              size: result.size,
              width: result.width,
              height: result.height,
            },
            uploading: false,
          })
        } catch (error) {
          patchStaged(conversationId, entry.id, {
            uploading: false,
            error: chatErrorMessage(error, FILE_TYPE_REFUSED),
          })
        }
      }
    },
    [canPost, conversationId, patchStaged, stageFiles, staged.length, upload],
  )

  const submit = useCallback(
    (text: string, advance: boolean) => {
      if (!canPost) return
      if (staged.some((item) => item.uploading)) return
      const ready = staged.filter((item) => item.attachmentId != null)
      const trimmed = text.trim()
      if (trimmed === '' && ready.length === 0) return

      const clientRef = newClientRef()
      const mentions = mentionable
        .filter((member) => trimmed.includes(`@${member.name}`))
        .map((member) => member.userId as number)

      const mentionTokens = [...new Set(Array.from(trimmed.matchAll(/@(\w+)/g), (match) => match[1]))]
      const unresolvedTokens = mentionTokens.filter(
        (token) =>
          !mentionable.some(
            (member) => mentions.includes(member.userId as number) && member.name.toLowerCase().startsWith(token.toLowerCase()),
          ),
      )
      for (const token of unresolvedTokens) {
        toast.error(CHAT_COPY.mentionDropped(token))
      }

      send.mutate(
        {
          conversationId,
          request: {
            clientRef,
            body: trimmed,
            attachmentIds: [...new Set(ready.map((item) => item.attachmentId as number))],
            mentions: [...new Set(mentions)],
            replyToMessageId: draft?.replyToMessageId ?? null,
          },
          optimistic: {
            clientRef,
            conversationId,
            body: trimmed,
            attachmentIds: ready.map((item) => item.attachmentId as number),
            attachments: ready.map((item) => item.attachment).filter((a) => a !== null),
            mentions,
            replyToMessageId: draft?.replyToMessageId ?? null,
            status: 'pending',
            error: null,
            sentAt: new Date().toISOString(),
          },
        },
        {
          onSuccess: (message) => {
            const dropped = mentionable.filter(
              (member) =>
                mentions.includes(member.userId as number) &&
                !(message.content?.mentions ?? []).includes(member.userId as number),
            )
            for (const member of dropped) {
              toast.error(CHAT_COPY.mentionDropped(member.name))
            }
            if (advance) onSendAndAdvance?.()
          },
          onError: (error) => {
            if ((error as { response?: { status?: number } })?.response?.status === 429) {
              setRateLimitCopy(chatErrorMessage(error, CHAT_COPY.failed))
              window.setTimeout(() => setRateLimitCopy(null), 10_000)
            }
          },
        },
      )

      clearComposer(conversationId)
    },
    [canPost, clearComposer, conversationId, draft?.replyToMessageId, mentionable, onSendAndAdvance, send, staged],
  )

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null) {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        setMentionQuery(null)
        return
      }
      if (event.key === 'Tab' || event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
        const match = mentionMatches[0]
        if (match) acceptMention(match)
        else if (event.key === 'Tab') setMentionQuery(null)
        return
      }
    }

    if (event.key !== 'Enter') return

    if (event.shiftKey && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      submit(body, true)
      return
    }

    if (!event.shiftKey && !event.metaKey && !event.ctrlKey) {
      event.preventDefault()
      submit(body, false)
    }
  }

  return (
    <div
      className="relative border-t border-line bg-surface"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault()
        void stage(Array.from(event.dataTransfer.files))
      }}
    >
      {mentionQuery !== null && (
        <MentionPopover members={mentionable} query={mentionQuery} onSelect={acceptMention} />
      )}
      {replyPreview && (
        <div className="flex items-start gap-2 border-b border-line-2 px-3 py-1.5 text-[12px] text-ink-3">
          <span className="truncate">{replyPreview}</span>
          <button
            type="button"
            onClick={() => setReplyTo(conversationId, null)}
            className="ml-auto shrink-0 text-ink-4 hover:text-ink-2"
          >
            Cancel
          </button>
        </div>
      )}
      {showChips && body.trim() === '' && staged.length === 0 && (
        <QuickReplyChips onSend={(chip) => submit(chip, false)} disabled={send.isPending || !canPost} />
      )}
      <ComposerAttachments conversationId={conversationId} />
      <div className="flex items-end gap-1.5 px-3 pb-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => {
            void stage(Array.from(event.target.files ?? []))
            event.target.value = ''
          }}
        />
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Attach a file"
          disabled={!canPost}
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip size={15} />
        </Button>
        <Textarea
          ref={textareaRef}
          value={body}
          placeholder={CHAT_COPY.composerPlaceholder}
          rows={1}
          disabled={!canPost}
          onChange={(event) => {
            setDraft(conversationId, event.target.value)
            const match = /@(\w*)$/.exec(event.target.value)
            setMentionQuery(match ? match[1] : null)
          }}
          onKeyDown={onKeyDown}
          onPaste={(event) => {
            const files = Array.from(event.clipboardData.files)
            if (files.length > 0) {
              event.preventDefault()
              void stage(files)
            }
          }}
          className="max-h-40 min-h-9 resize-none py-2 text-[13px]"
        />
        <Button
          size="icon-sm"
          aria-label="Send"
          disabled={
            !canPost || hasUploadingAttachment || (body.trim() === '' && staged.every((item) => item.attachmentId === null))
          }
          onClick={() => submit(body, false)}
        >
          <SendHorizontal size={15} />
        </Button>
      </div>
      {rateLimitCopy && <p className="px-3 pb-1.5 text-[11.5px] text-warn">{rateLimitCopy}</p>}
      <p className="px-3 pb-2 text-[11px] text-ink-4">{canPost ? hintText : CHAT_COPY.cannotPost}</p>
    </div>
  )
}
